'use client';

import React, { useCallback, useRef } from 'react';

import styles from './SectionStepperTabs.module.scss';

export type SectionStepperTabItem = {
  id: string;
  label: string;
  completed?: boolean;
  disabled?: boolean;
  metaLabel?: string;
};

export type SectionStepperNavigationMode = 'free' | 'sequential';

export type SectionStepperTabsProps = {
  sections: SectionStepperTabItem[];
  activeId: string;
  onSectionChange: (id: string) => void;
  className?: string;
  sectionClassName?: string;
  navigationMode?: SectionStepperNavigationMode;
  allowBackNavigation?: boolean;
  'aria-label'?: string;
};

export function SectionStepperTabs({
  sections,
  activeId,
  onSectionChange,
  className,
  sectionClassName,
  navigationMode = 'free',
  allowBackNavigation = true,
  'aria-label': ariaLabel = 'Section stepper tabs',
}: SectionStepperTabsProps) {
  const sectionRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const activeIndex = sections.findIndex((section) => section.id === activeId);

  const canSelectIndex = useCallback(
    (targetIndex: number) => {
      const section = sections[targetIndex];

      if (!section || section.disabled) {
        return false;
      }

      if (targetIndex === activeIndex) {
        return true;
      }

      if (navigationMode === 'free') {
        return true;
      }

      if (targetIndex < activeIndex) {
        return allowBackNavigation;
      }

      return sections
        .slice(0, targetIndex)
        .every((step) => Boolean(step.completed));
    },
    [activeIndex, allowBackNavigation, navigationMode, sections],
  );

  const focusByIndex = useCallback(
    (index: number) => {
      const section = sections[index];
      if (!section) {
        return;
      }
      sectionRefs.current.get(section.id)?.focus();
    },
    [sections],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, currentIndex: number) => {
      const isPreviousKey =
        event.key === 'ArrowLeft' || event.key === 'ArrowUp';
      const isNextKey = event.key === 'ArrowRight' || event.key === 'ArrowDown';

      if (
        !isPreviousKey &&
        !isNextKey &&
        event.key !== 'Home' &&
        event.key !== 'End'
      ) {
        return;
      }

      event.preventDefault();

      if (event.key === 'Home') {
        const firstSelectableIndex = sections.findIndex((_, index) =>
          canSelectIndex(index),
        );
        if (firstSelectableIndex >= 0) {
          focusByIndex(firstSelectableIndex);
        }
        return;
      }

      if (event.key === 'End') {
        for (let index = sections.length - 1; index >= 0; index -= 1) {
          if (canSelectIndex(index)) {
            focusByIndex(index);
            break;
          }
        }
        return;
      }

      const delta = isPreviousKey ? -1 : 1;
      for (
        let index = currentIndex + delta;
        index >= 0 && index < sections.length;
        index += delta
      ) {
        if (canSelectIndex(index)) {
          focusByIndex(index);
          break;
        }
      }
    },
    [canSelectIndex, focusByIndex, sections],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className={`${styles.sectionStepperTabs} ${className || ''}`}>
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={styles.tabsContainer}
      >
        {sections.map((section, index) => {
          const isActive = activeId === section.id;
          const isSelectable = canSelectIndex(index);
          const state = isActive
            ? 'active'
            : section.completed
              ? 'completed'
              : 'pending';

          return (
            <button
              key={section.id}
              type="button"
              role="tab"
              aria-label={section.label}
              aria-selected={isActive}
              aria-disabled={!isSelectable}
              id={`section-stepper-tab-${index}`}
              tabIndex={isActive ? 0 : -1}
              // disabled={!isSelectable}
              data-state={state}
              className={`${styles.stepButton} ${sectionClassName || ''}`}
              onClick={() => {
                if (isSelectable) {
                  onSectionChange(section.id);
                }
              }}
              onKeyDown={(event) => handleKeyDown(event, index)}
              ref={(element) => {
                if (element) {
                  sectionRefs.current.set(section.id, element);
                } else {
                  sectionRefs.current.delete(section.id);
                }
              }}
            >
              <span className={styles.stepIndicator} aria-hidden>
                {section.completed ? '✓' : index + 1}
              </span>

              <span className={styles.stepDetails}>
                <span className={styles.stepLabel}>{section.label}</span>
                <span className={styles.stepMeta}>
                  {section.metaLabel ||
                    `Step ${index + 1} of ${sections.length}`}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
